import React, { useEffect, useState } from 'react';
import {
  Clock, Play, CheckCircle, ShieldCheck, ShieldAlert, AlertCircle,
  MapPin, MapPinOff, RefreshCw, Calendar, User, UserCircle, Check, Download, Edit2, X,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { fmtTime12, isoDate } from '../../utils/time';

// Wall-clock "HH:MI" (24h, from the server's to_char) → 12h "h:mm am/pm". No Date/TZ involved.
const hm12 = (hm) => {
  if (!hm) return null;
  const [h, m] = String(hm).split(':').map(Number);
  if (isNaN(h)) return hm;
  const ap = h < 12 ? 'am' : 'pm';
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${ap}`;
};

// Metres → "19 m" / "1.2 km".
const fmtDist = (d) => (d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${d} m`);

// Geofence state of ONE clock (in or out). clock_in_verified / clock_out_verified both DEFAULT
// false, so a clock with no GPS fix also reads false — "no GPS" must come from the distance,
// never from verified === false, and is shown neutral grey, never as flagged.
//   'verified' | 'manual' (verified, no distance — admin set it) | 'flagged' (outside) | 'nogps' | null (not clocked)
const geoState = (clocked, distance, verified) => {
  if (!clocked) return null;
  if (verified === true) return distance == null ? 'manual' : 'verified';
  return distance == null ? 'nogps' : 'flagged';
};
const GEO_LABEL = { verified: 'Verified', manual: 'Verified (manual)', flagged: 'Flagged', nogps: 'No GPS' };

// "In 5 m" / "Out 19 m" — green within the org's radius, amber beyond it, grey when no GPS.
function DistLine({ label, distance, radius }) {
  const tag = <span className="w-6 text-[10px] font-semibold text-slate-400 font-sans">{label}</span>;
  if (distance == null) {
    return (
      <div className="flex items-center gap-1 text-slate-400" title={`Clock-${label.toLowerCase()}: no GPS location captured`}>
        {tag}<MapPinOff size={11} /> No GPS
      </div>
    );
  }
  const inside = distance <= radius;
  return (
    <div className={`flex items-center gap-1 font-mono ${inside ? 'text-emerald-600' : 'text-amber-600'}`}
      title={`Clock-${label.toLowerCase()} ${fmtDist(distance)} from the client — ${inside ? 'inside' : 'outside'} the ${radius} m geofence`}>
      {tag}<MapPin size={11} /> {fmtDist(distance)}
    </div>
  );
}

function VerifyLine({ label, state, reason }) {
  const tag = <span className="w-6 text-[10px] font-semibold text-slate-400">{label}</span>;
  if (state === 'verified' || state === 'manual') {
    return (
      <div className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
        {tag}<ShieldCheck size={11} /> {GEO_LABEL[state]}
      </div>
    );
  }
  if (state === 'flagged') {
    return (
      <div className="flex items-center gap-1 text-amber-600 text-xs font-medium" title={reason || ''}>
        {tag}<ShieldAlert size={11} /> Flagged
        {reason && <span className="text-slate-400 text-[10px] ml-0.5 truncate max-w-[120px]">({reason})</span>}
      </div>
    );
  }
  if (state === 'nogps') {
    return <div className="flex items-center gap-1 text-slate-400 text-xs">{tag}<MapPinOff size={11} /> No GPS</div>;
  }
  return null;
}

// ── Manual Clock Modal ────────────────────────────────────────────────────────
function ManualClockModal({ shift, onSaved, onClose }) {
  const shiftDate = (shift.shift_date || '').slice(0, 10) || isoDate();
  // Clock times are timezone-NAIVE wall-clock — never run them through new Date()/toISOString
  // (that shifts by the browser offset and drifts on every re-save). Slice the stored value in.
  const naive = (v, fb) => v ? String(v).replace('T', ' ').slice(0, 16).replace(' ', 'T') : fb;
  const [clockIn,  setClockIn]  = useState(naive(shift.clock_in_time,  `${shiftDate}T${shift.start_time?.slice(0,5) || '09:00'}`));
  const [clockOut, setClockOut] = useState(naive(shift.clock_out_time, `${shiftDate}T${shift.end_time?.slice(0,5) || '17:00'}`));
  const [reason,   setReason]   = useState('');
  const [saving,   setSaving]   = useState(false);

  const hoursPreview = (() => {
    const p = s => { const m = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s || ''); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null; };
    const a = p(clockIn), b = p(clockOut);
    if (a == null || b == null) return null;
    const ms = b - a;
    return ms > 0 ? (ms / 3600000).toFixed(2) : null;
  })();

  async function save() {
    if (!clockIn) return toast.error('Clock-in time is required');
    setSaving(true);
    try {
      await api.post(`/evv/shifts/${shift.id}/manual-clock`, {
        clock_in_time:  clockIn,                 // naive wall-clock, stored as-is (no TZ shift)
        clock_out_time: clockOut || null,
        reason: reason || 'Admin manual entry',
      });
      toast.success('Times saved — timesheet created/updated, pending approval');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-96 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Set Clock-In / Out</h3>
            <p className="text-xs text-slate-500 mt-0.5">{shift.staff_name} · {shiftDate}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800 mb-4">
          This will create/update the timesheet and set it to <strong>Pending Approval</strong>. Admin must approve via Timesheets.
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Clock-In Time *</label>
            <input type="datetime-local" value={clockIn} onChange={e => setClockIn(e.target.value)}
              className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Clock-Out Time</label>
            <input type="datetime-local" value={clockOut} onChange={e => setClockOut(e.target.value)}
              className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            {hoursPreview && (
              <p className="text-xs text-emerald-600 mt-1 font-medium">{hoursPreview} hours</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason / Notes</label>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Worker forgot to clock in"
              className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary btn-sm">
            {saving ? 'Saving…' : 'Save & Send to Timesheet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EVVDashboard() {
  // ?date=YYYY-MM-DD opens a given day (the dashboard's clock-in alerts link here to fix times).
  const [date, setDate]     = useState(() => {
    const q = new URLSearchParams(window.location.search).get('date');
    return /^\d{4}-\d{2}-\d{2}$/.test(q || '') ? q : isoDate();
  });
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [filter, setFilter] = useState('all');
  const [nextRefresh, setNextRefresh] = useState(30);
  const [manualClockShift, setManualClockShift] = useState(null);
  const [view, setView] = useState('day');               // 'day' | 'month'
  const [monthData, setMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const monthStr = date.slice(0, 7);

  useEffect(() => {
    if (view !== 'month') return;
    setMonthLoading(true);
    api.get(`/evv/month?month=${monthStr}`)
      .then(r => setMonthData(r.data))
      .catch(() => toast.error('Failed to load month'))
      .finally(() => setMonthLoading(false));
    // eslint-disable-next-line
  }, [view, monthStr]);

  const load = (d) => {
    const target = d ?? date;
    setLoad(true);
    api.get(`/evv/dashboard?date=${target}`)
      .then(r => { setData(r.data); setNextRefresh(30); })
      .catch(() => toast.error('Failed to load EVV dashboard'))
      .finally(() => setLoad(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  // Auto-refresh every 30s with countdown
  useEffect(() => {
    const interval = setInterval(() => load(), 30000);
    const countdown = setInterval(() => setNextRefresh(n => (n > 1 ? n - 1 : 30)), 1000);
    return () => { clearInterval(interval); clearInterval(countdown); };
    // eslint-disable-next-line
  }, [date]);

  // which: 'in' | 'out' | 'both' — which clock(s) the admin is manually marking verified.
  const verifyShift = async (timesheetId, which = 'in') => {
    if (!timesheetId) { toast.error('No timesheet for this shift'); return; }
    try {
      await api.post(`/evv/${timesheetId}/verify`, { which });
      toast.success(which === 'both' ? 'Clock-in and clock-out marked as verified'
        : which === 'out' ? 'Clock-out marked as verified' : 'Clock-in marked as verified');
      load();
    } catch { toast.error('Verify failed'); }
  };

  // CSV export
  const exportCSV = () => {
    if (!data?.shifts?.length) return;
    const headers = ['Time', 'Staff', 'Client', 'Status', 'Clock-In', 'Clock-Out',
      'Clock-In Distance', 'Clock-In Verified', 'Clock-Out Distance', 'Clock-Out Verified', 'Address'];
    const rows = data.shifts.map(s => {
      const inSt  = geoState(!!s.clock_in_time,  s.clock_in_distance_m,  s.clock_in_verified);
      const outSt = geoState(!!s.clock_out_time, s.clock_out_distance_m, s.clock_out_verified);
      return [
        `${fmtTime12(s.start_time)} – ${fmtTime12(s.end_time)}`,
        s.staff_name || '',
        s.client_name || '',
        s.ts_status || '',
        s.clock_in_hm || '',
        s.clock_out_hm || '',
        s.clock_in_distance_m != null ? `${s.clock_in_distance_m}m` : '',
        inSt ? GEO_LABEL[inSt] : '',
        s.clock_out_distance_m != null ? `${s.clock_out_distance_m}m` : '',
        outSt ? GEO_LABEL[outSt] : '',
        s.address || '',
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `evv_${date}.csv`;
    a.click();
  };

  const tiles = data?.tiles || {};
  const shifts = data?.shifts || [];
  const radius = data?.geofence_radius_m || 400;   // org geofence (Financials → Settings)

  const TILES = [
    { key: 'scheduled',      label: 'Scheduled',        value: tiles.scheduled || 0,      icon: Clock,       color: 'slate',   desc: 'Not yet started' },
    { key: 'in_progress',    label: 'In Progress',      value: tiles.in_progress || 0,    icon: Play,        color: 'blue',    desc: 'Clocked in, not yet out' },
    { key: 'completed',      label: 'Completed',        value: tiles.completed || 0,      icon: CheckCircle, color: 'emerald', desc: 'Clocked out' },
    { key: 'verified',       label: 'Verified',         value: tiles.verified || 0,       icon: ShieldCheck, color: 'teal',    desc: `Clock-in verified · ${tiles.out_verified || 0} out` },
    { key: 'unverified',     label: 'Outside Geofence', value: tiles.unverified || 0,     icon: ShieldAlert, color: 'amber',   desc: 'Clock-in flagged' },
    { key: 'out_unverified', label: 'Out: Outside',     value: tiles.out_unverified || 0, icon: ShieldAlert, color: 'orange',  desc: 'Clock-out flagged' },
    { key: 'no_gps',         label: 'No GPS',           value: tiles.no_gps || 0,         icon: MapPinOff,   color: 'gray',    desc: 'Clock-in had no location' },
    { key: 'missed',         label: 'Missed',           value: tiles.missed || 0,         icon: AlertCircle, color: 'red',     desc: 'No clock-in, past start' },
  ];

  const filtered = shifts.filter(s => {
    if (filter === 'all')         return true;
    if (filter === 'in_progress') return s.clock_in_time && !s.clock_out_time;
    if (filter === 'completed')   return s.clock_in_time && s.clock_out_time;
    if (filter === 'verified')    return s.clock_in_verified === true;
    if (filter === 'unverified')  return geoState(!!s.clock_in_time, s.clock_in_distance_m, s.clock_in_verified) === 'flagged';
    if (filter === 'out_unverified') return geoState(!!s.clock_out_time, s.clock_out_distance_m, s.clock_out_verified) === 'flagged';
    if (filter === 'no_gps')      return geoState(!!s.clock_in_time, s.clock_in_distance_m, s.clock_in_verified) === 'nogps';
    if (filter === 'scheduled')   return !s.clock_in_time;
    if (filter === 'missed') {
      const past = new Date(`${s.shift_date?.split('T')[0]}T${s.start_time}`) < new Date();
      return !s.clock_in_time && past;
    }
    return true;
  });

  // Rate over clock-ins with a verdict (verified vs outside geofence) — no-GPS clocks are unknown, not failures.
  const verifiedTotal = (tiles.verified || 0) + (tiles.unverified || 0);
  const verifiedRate  = verifiedTotal > 0 ? Math.round((tiles.verified / verifiedTotal) * 100) : null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">EVV Dashboard</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Electronic Visit Verification — geofence-checked clock-ins and clock-outs
            {data && <span className="text-slate-400"> · {radius} m radius</span>}
            {verifiedRate != null && (
              <span className={`ml-3 inline-flex items-center gap-1 font-semibold text-xs px-2 py-0.5 rounded-full ${
                verifiedRate >= 90 ? 'bg-emerald-50 text-emerald-700' :
                verifiedRate >= 70 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
              }`}>
                <ShieldCheck size={10} /> {verifiedRate}% verified
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-medium">
            <button onClick={() => setView('day')} className={`px-3 py-1.5 ${view === 'day' ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Day</button>
            <button onClick={() => setView('month')} className={`px-3 py-1.5 ${view === 'month' ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Month</button>
          </div>
          {view === 'day' && <>
            <button onClick={exportCSV} disabled={!data?.shifts?.length}
              className="btn-secondary btn-sm flex items-center gap-1.5 disabled:opacity-40">
              <Download size={13} /> Export CSV
            </button>
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="date" value={date} onChange={e => { setDate(e.target.value); setFilter('all'); }}
                className="input-field pl-9 !w-44 text-sm !py-2" />
            </div>
            <button onClick={() => load()} disabled={loading} className="btn-secondary btn-sm flex items-center gap-1.5">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Loading…' : `Refresh (${nextRefresh}s)`}
            </button>
          </>}
        </div>
      </div>

      {view === 'month' ? (
        <MonthView month={monthStr} data={monthData} loading={monthLoading}
          onPickDay={d => { setView('day'); setDate(d); setFilter('all'); }}
          onMonth={dir => { const [yy, mm] = monthStr.split('-').map(Number); const nd = new Date(Date.UTC(yy, mm - 1 + dir, 1)); setDate(nd.toISOString().slice(0, 10)); }} />
      ) : (<>
      {/* Status tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {TILES.map(t => {
          const Icon = t.icon;
          const isActive = filter === t.key;
          const ringColor = {
            slate: 'border-slate-300', blue: 'border-blue-300', emerald: 'border-emerald-300',
            teal: 'border-teal-300', amber: 'border-amber-300', orange: 'border-orange-300',
            gray: 'border-slate-300', red: 'border-red-300',
          }[t.color];
          const iconBg = {
            slate: 'bg-slate-100 text-slate-600', blue: 'bg-blue-100 text-blue-600',
            emerald: 'bg-emerald-100 text-emerald-600', teal: 'bg-teal-100 text-teal-600',
            amber: 'bg-amber-100 text-amber-600', orange: 'bg-orange-100 text-orange-600',
            gray: 'bg-slate-100 text-slate-400', red: 'bg-red-100 text-red-600',
          }[t.color];
          return (
            <button key={t.key}
              onClick={() => setFilter(isActive ? 'all' : t.key)}
              className={`card p-4 text-left transition-all hover:shadow-md ${isActive ? `border-2 ${ringColor} shadow-md` : ''}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${iconBg}`}>
                <Icon size={16} />
              </div>
              <div className="text-2xl font-bold text-slate-800">{t.value}</div>
              <div className="text-xs font-semibold text-slate-700 mt-0.5">{t.label}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Filter indicator */}
      {filter !== 'all' && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Filtered to:</span>
          <span className="bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full font-medium">
            {TILES.find(t => t.key === filter)?.label}
          </span>
          <button onClick={() => setFilter('all')} className="text-slate-400 hover:text-slate-700">×</button>
        </div>
      )}

      {/* Shifts table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <ShieldCheck size={32} className="mx-auto opacity-40 mb-2" />
            <p>No shifts in this view</p>
            {filter !== 'all' && (
              <button onClick={() => setFilter('all')} className="mt-2 text-xs text-primary-600 hover:underline">
                Clear filter
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Staff</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Clock-In</th>
                  <th>Clock-Out</th>
                  <th>Distance</th>
                  <th>Verified</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const inSt  = geoState(!!s.clock_in_time,  s.clock_in_distance_m,  s.clock_in_verified);
                  const outSt = geoState(!!s.clock_out_time, s.clock_out_distance_m, s.clock_out_verified);
                  // Manual verify: any clock that is flagged or has no GPS can be marked verified.
                  const inNeeds  = inSt === 'flagged' || inSt === 'nogps';
                  const outNeeds = outSt === 'flagged' || outSt === 'nogps';
                  const which = inNeeds && outNeeds ? 'both' : inNeeds ? 'in' : outNeeds ? 'out' : null;
                  return (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="text-xs">
                      <div className="font-medium">{fmtTime12(s.start_time)} – {fmtTime12(s.end_time)}</div>
                      {s.address && <div className="text-slate-400 text-[10px] truncate max-w-[100px]" title={s.address}>{s.address}</div>}
                    </td>
                    <td className="text-sm">
                      {s.staff_name ? (
                        <span className="inline-flex items-center gap-1.5">
                          <UserCircle size={13} className="text-slate-400" />
                          {s.staff_name}
                        </span>
                      ) : <span className="text-slate-300 text-xs">Unassigned</span>}
                    </td>
                    <td className="text-sm">
                      {s.client_name ? (
                        <span className="inline-flex items-center gap-1.5">
                          <User size={13} className="text-slate-400" />
                          {s.client_name}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td><StatusBadge shift={s} /></td>
                    <td className="text-xs font-mono">
                      {s.clock_in_hm
                        ? hm12(s.clock_in_hm)
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="text-xs font-mono">
                      {s.clock_out_hm
                        ? hm12(s.clock_out_hm)
                        : s.clock_in_time
                          ? <span className="text-blue-400 text-[10px]">In progress</span>
                          : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="text-xs">
                      {s.clock_in_time ? (
                        <div className="space-y-0.5 whitespace-nowrap">
                          <DistLine label="In" distance={s.clock_in_distance_m} radius={radius} />
                          {s.clock_out_time && <DistLine label="Out" distance={s.clock_out_distance_m} radius={radius} />}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td>
                      {s.clock_in_time ? (
                        <div className="space-y-0.5 whitespace-nowrap">
                          <VerifyLine label="In" state={inSt} reason={s.clock_in_reason} />
                          {outSt && <VerifyLine label="Out" state={outSt} />}
                        </div>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {which && s.timesheet_id && (
                          <button
                            onClick={() => verifyShift(s.timesheet_id, which)}
                            className="text-xs text-primary-600 hover:underline flex items-center gap-1 whitespace-nowrap"
                            title={which === 'both' ? 'Mark clock-in and clock-out as verified'
                              : which === 'out' ? 'Mark clock-out as verified' : 'Mark clock-in as verified'}
                          >
                            <Check size={11} /> Override {which === 'both' ? 'in + out' : which}
                          </button>
                        )}
                        {/* Set Times: show for missed, clocked-in-only, or any shift missing clock data */}
                        {(!s.clock_in_time || !s.clock_out_time) && (
                          <button
                            onClick={() => setManualClockShift(s)}
                            className="text-xs text-amber-600 hover:underline flex items-center gap-1 whitespace-nowrap"
                            title="Admin: manually set clock-in / clock-out"
                          >
                            <Edit2 size={11} /> Set Times
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}

      {manualClockShift && (
        <ManualClockModal
          shift={manualClockShift}
          onSaved={() => { setManualClockShift(null); load(); }}
          onClose={() => setManualClockShift(null)}
        />
      )}
    </div>
  );
}

// ── Month view: whole-month coverage (who clocked in vs not) + drill into a day ──
function MonthView({ month, data, loading, onPickDay, onMonth }) {
  const t = data?.tiles || {};
  const dayMap = Object.fromEntries((data?.days || []).map(d => [d.date, d]));
  const [y, m] = month.split('-').map(Number);
  const startDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();          // 0=Sun
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const monthLabel = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`);
  const MT = [
    { label: 'Shifts', value: t.total || 0, cls: 'text-slate-800' },
    { label: 'Clocked in', value: t.clocked_in || 0, cls: 'text-blue-600' },
    { label: 'Completed', value: t.completed || 0, cls: 'text-emerald-600' },
    { label: 'Verified in', value: t.verified || 0, cls: 'text-teal-600' },
    { label: 'Verified out', value: t.out_verified || 0, cls: 'text-teal-600' },
    { label: 'Missed', value: t.missed || 0, cls: 'text-red-600' },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => onMonth(-1)} className="btn-secondary btn-sm px-2">‹</button>
        <span className="font-semibold text-slate-700 w-44 text-center">{monthLabel}</span>
        <button onClick={() => onMonth(1)} className="btn-secondary btn-sm px-2">›</button>
        {loading && <span className="text-xs text-slate-400">Loading…</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {MT.map(x => (
          <div key={x.label} className="card p-4">
            <div className={`text-2xl font-bold ${x.cls}`}>{x.value}</div>
            <div className="text-xs font-semibold text-slate-600 mt-0.5">{x.label}</div>
          </div>
        ))}
      </div>
      <div className="card p-4">
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-slate-400 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((dt, i) => {
            if (!dt) return <div key={i} />;
            const d = dayMap[dt];
            const has = d && d.total > 0;
            let cls = 'bg-slate-50 text-slate-400 border-slate-100';
            if (has) cls = d.missed > 0 ? 'bg-red-50 text-red-700 border-red-200'
              : d.clocked_in >= d.total ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200';
            return (
              <button key={i} onClick={() => has && onPickDay(dt)} disabled={!has}
                className={`min-h-[58px] rounded-lg border p-1.5 text-left transition-all ${cls} ${has ? 'hover:shadow-md' : 'cursor-default'}`}>
                <div className="text-[11px] font-bold">{Number(dt.slice(8))}</div>
                {has && <div className="text-[10px] font-medium mt-0.5">{d.clocked_in}/{d.total} in</div>}
                {has && d.missed > 0 && <div className="text-[9px] text-red-600 font-semibold">{d.missed} missed</div>}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-400">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-200" /> all clocked in</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-200" /> partial</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-200" /> missed</span>
          <span className="ml-auto">Click a day to see details</span>
        </div>
      </div>
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-700">Who clocked in this month</h3></div>
        {(data?.staff || []).length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No shifts this month</div>
        ) : (
          <div className="table-wrapper"><table className="table-base">
            <thead><tr><th>Staff</th><th className="text-right">Shifts</th><th className="text-right">Clocked in</th><th className="text-right">Missed</th><th className="text-right">Verified in</th><th className="text-right">Verified out</th><th className="text-right">Coverage</th></tr></thead>
            <tbody>
              {(data?.staff || []).map(s => {
                const pct = s.total ? Math.round((s.clocked_in / s.total) * 100) : 0;
                return (
                  <tr key={s.staff_id || s.staff_name} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-slate-700">{s.staff_name}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{s.total}</td>
                    <td className="px-4 py-2.5 text-right text-blue-600 font-medium">{s.clocked_in}</td>
                    <td className="px-4 py-2.5 text-right">{s.missed > 0 ? <span className="text-red-600 font-semibold">{s.missed}</span> : <span className="text-slate-300">0</span>}</td>
                    <td className="px-4 py-2.5 text-right text-teal-600">{s.verified}</td>
                    <td className="px-4 py-2.5 text-right text-teal-600">{s.out_verified || 0}</td>
                    <td className="px-4 py-2.5 text-right"><span className={`font-bold ${pct >= 90 ? 'text-emerald-600' : pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ shift: s }) {
  if (!s.clock_in_time) {
    const past = new Date(`${s.shift_date?.split('T')[0]}T${s.start_time}`) < new Date();
    return past
      ? <span className="bg-red-100 text-red-700 text-[10px] px-2 py-0.5 rounded-full font-medium">Missed</span>
      : <span className="bg-slate-100 text-slate-600 text-[10px] px-2 py-0.5 rounded-full font-medium">Scheduled</span>;
  }
  if (!s.clock_out_time) {
    return <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-medium">In progress</span>;
  }
  return <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-medium">Completed</span>;
}
